import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { ampli } from '@/ampli';
import { type ConnectedWallet } from '@privy-io/react-auth';
import { type ConnectedStandardSolanaWallet } from '@privy-io/react-auth/solana';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { GetEnsNameReturnType } from 'viem';
import { useEnsName } from 'wagmi';

import { TextSkeleton } from '@/components/Skeleton';

import { CHAIN_IDS, NETWORK_NAME_PER_CHAIN_ID } from '@/constants/chain';

import { useFiatDisplay } from '@/hooks/useFiatDisplay';
import { useMatchaWallets } from '@/hooks/useMatchaWallets';
import { useWalletPortfolio } from '@/hooks/useWalletPortfolio';

import { btnStyles, buttons } from '@/styles/buttons';
import { popover } from '@/styles/popover';
import { fontStyles } from '@/styles/typography';

import { AvatarStack } from '@/ui/Avatars/AvatarStack';
import { ChainAvatar } from '@/ui/Avatars/ChainAvatar';
import { WalletAvatar } from '@/ui/Avatars/WalletAvatar';
import { ChevronDownIcon } from '@/ui/Icons/ChevronDownIcon';
import { DisconnectIcon } from '@/ui/Icons/DisconnectIcon';
import { Tooltip } from '@/ui/Tooltip';

import { formatShortAddress } from '@/utils/address';
import { classNames } from '@/utils/classnames';
import { EthereumAddress, SolanaAddress } from '@/utils/models';
import type { WalletPortfolio } from '@/utils/zerion/types';

export const AccountButtonDropdown = () => {
  const popoverStyles = popover();
  const [open, setOpen] = useState<boolean>(false);
  const { solanaWallet, ethereumWallet, connect, disconnectEthereum, disconnectSolana } =
    useMatchaWallets();
  const [selectedOptionIndex, setSelectedOptionIndex] = useState(-1);
  const connectButtonRef = useRef<HTMLButtonElement>(null);

  const activeWallets = useMemo(
    () => [ethereumWallet, solanaWallet].filter((val) => val !== undefined),
    [solanaWallet, ethereumWallet],
  );
  const { data: combinedPortfolio, isLoading: isPortfolioLoading } = useWalletPortfolio({
    evmAddress: ethereumWallet?.address,
    solanaAddress: solanaWallet?.address,
    enabled: open,
  });
  const { evmPortfolio, solanaPortfolio } = useMemo(() => {
    if (!combinedPortfolio) return {};

    const evmChainDistribution: Record<number, number> = {};
    for (const [chainId, value] of Object.entries(combinedPortfolio.chainDistribution)) {
      const numericChainId = Number(chainId);
      if (numericChainId === CHAIN_IDS.SOLANA) continue;
      evmChainDistribution[numericChainId] = value;
    }

    const solanaValue = combinedPortfolio.chainDistribution[CHAIN_IDS.SOLANA] ?? 0;
    const evmTotalValue = Math.max(0, combinedPortfolio.totalValue - solanaValue);
    const solanaChainDistribution: Record<number, number> = {};
    if (solanaValue > 0) solanaChainDistribution[CHAIN_IDS.SOLANA] = solanaValue;
    return {
      evmPortfolio: { totalValue: evmTotalValue, chainDistribution: evmChainDistribution },
      solanaPortfolio: {
        totalValue: solanaValue,
        chainDistribution: solanaChainDistribution,
      },
    };
  }, [combinedPortfolio]);

  useEffect(() => {
    if (selectedOptionIndex === activeWallets.length) {
      connectButtonRef.current?.focus();
    }
  }, [activeWallets, selectedOptionIndex]);

  const onDisconnect = useCallback(
    (wallet: ConnectedStandardSolanaWallet | ConnectedWallet) => {
      if ('standardWallet' in wallet) {
        void disconnectSolana();
      } else {
        void disconnectEthereum();
      }
      ampli.walletDisconnectClicked({ source: 'primary_button', ui_location: 'account_button' });
    },
    [disconnectEthereum, disconnectSolana],
  );

  const onConnect = () => {
    ampli.walletConnectClicked({
      source: 'other_wallet_option',
      ui_location: 'other_wallet_option',
    });
    connectButtonRef.current?.blur();
    connect();
    setTimeout(() => {
      // focus the modal once the modal is animated in
      (
        document.querySelector(
          '#privy-modal-content button.login-method-button:first-child',
        ) as HTMLButtonElement
      )?.focus();
    }, 200);
    // we need to close the dropdown to allow the privy modal becoming scrollable
    setOpen(false);
    setSelectedOptionIndex(-1);
  };

  return (
    <>
      <DropdownMenu.Root open={open} onOpenChange={setOpen} modal={false}>
        <DropdownTrigger onConnect={onConnect} />
        <DropdownMenu.Portal>
          <DropdownMenu.Content
            align="end"
            className={classNames(popoverStyles.base(), popoverStyles.wide(), 'p-2')}
          >
            {activeWallets.map((wallet, index) => (
              <DropdownMenuItem
                key={wallet.address}
                wallet={wallet}
                portfolio={'standardWallet' in wallet ? solanaPortfolio : evmPortfolio}
                isPortfolioLoading={isPortfolioLoading}
                selected={selectedOptionIndex === index}
                onPrevious={() => {
                  if (index === 0) {
                    // One of the options is the connect button, so we want to allow length + 1
                    setSelectedOptionIndex(activeWallets.length);
                  } else {
                    setSelectedOptionIndex(Math.max(0, index - 1));
                  }
                }}
                onNext={() => {
                  // One of the options is the connect button, so we want to allow length + 1
                  setSelectedOptionIndex(Math.min(activeWallets.length, index + 1));
                }}
                onDisconnect={onDisconnect}
              />
            ))}
            <div>
              <button
                tabIndex={selectedOptionIndex === activeWallets.length ? 0 : -1}
                onClick={onConnect}
                style={{ width: '100%' }}
                className={buttons({
                  size: 'small',
                  shape: 'pill',
                  color: 'transparent',
                  class: [
                    'bg-token-alphalight-200',
                    'h-10',
                    'mt-2',
                    'text-token-gray-1600',
                    'hover:bg-token-alphalight-300',
                    'active:bg-token-alphalight-400',
                    'active:scale-[0.98]',
                    'focus-visible:outline-[3px]',
                    'focus-visible:outline-solid',
                    'focus-visible:outline-token-alphalight-500',
                    'disabled:opacity-[0.5]',
                  ],
                })}
                ref={connectButtonRef}
                onKeyDown={(event) => {
                  if (event.key === 'ArrowDown') {
                    setSelectedOptionIndex(0);
                  } else if (event.key === 'ArrowUp') {
                    setSelectedOptionIndex(Math.max(selectedOptionIndex - 1, 0));
                  } else if (event.key === 'Enter') {
                    onConnect();
                  }
                }}
              >
                {solanaWallet && ethereumWallet
                  ? 'Switch to another wallet'
                  : 'Connect another wallet'}
              </button>
            </div>
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
    </>
  );
};

function ShortenedAddress({
  wallet,
  isEthereum,
  className,
}: {
  wallet?: ConnectedStandardSolanaWallet | ConnectedWallet;
  isEthereum: boolean;
  className?: string;
}) {
  const [showTooltip, setShowTooltip] = useState(false);
  const [clickedButton, setClickedButton] = useState(false);
  const onCopyAddress = useCallback(() => {
    if (!wallet) return;
    navigator.clipboard.writeText(wallet.address);
    setClickedButton(true);
    setTimeout(() => {
      setShowTooltip(false);

      // re-enabling interactivity after tooltip was animated out
      setTimeout(() => {
        setClickedButton(false);
      }, 300);
    }, 2000);
  }, [wallet]);
  if (!wallet) return null;
  return (
    <Tooltip.Provider>
      <Tooltip.Root open={showTooltip}>
        <Tooltip.Trigger asChild>
          <button
            onMouseEnter={() => setShowTooltip(true)}
            onFocus={() => setShowTooltip(true)}
            onMouseOut={() => {
              if (clickedButton) return;
              setShowTooltip(false);
            }}
            onBlur={() => {
              if (clickedButton) return;
              setShowTooltip(false);
            }}
            aria-label={`Wallet ending with ${wallet.address.slice(-4)}, click to copy`}
            className={buttons({
              iconOnly: true,
              color: 'transparent',
              shape: 'rounded',
              class: ['cursor-pointer p-1.5 mb-0.5 -ml-1 h-fit w-fit', className],
            })}
            onClick={onCopyAddress}
          >
            {shortenAddress(wallet.address, isEthereum ? 6 : 4, 4)}
          </button>
        </Tooltip.Trigger>
        <Tooltip.Portal>
          <Tooltip.Content>{clickedButton ? 'Copied' : 'Copy Address'} </Tooltip.Content>
        </Tooltip.Portal>
      </Tooltip.Root>
    </Tooltip.Provider>
  );
}

function shortenAddress(address: string, startLength = 5, endLength = 5) {
  return `${address.slice(0, startLength)}…${address.slice(-endLength)}`;
}

const MAX_VISIBLE_CHAINS = 4;

function DropdownMenuItem({
  wallet,
  portfolio,
  isPortfolioLoading,
  onDisconnect,
  onNext,
  onPrevious,
  selected,
}: {
  wallet: ConnectedStandardSolanaWallet | ConnectedWallet;
  portfolio?: Pick<WalletPortfolio, 'totalValue' | 'chainDistribution'>;
  isPortfolioLoading: boolean;
  onDisconnect: (wallet: ConnectedStandardSolanaWallet | ConnectedWallet) => void;
  onNext?: () => void;
  onPrevious?: () => void;
  selected?: boolean;
}) {
  const { formatFiat } = useFiatDisplay();
  const isSolana = 'standardWallet' in wallet;
  const disconnectRef = useRef<HTMLButtonElement>(null);

  const chainDistribution = portfolio?.chainDistribution;
  const sortedChainIds = useMemo(() => {
    if (!chainDistribution) return [];
    return Object.entries(chainDistribution)
      .sort(([, a], [, b]) => b - a)
      .map(([id]) => Number(id));
  }, [chainDistribution]);

  const visibleChains = sortedChainIds.slice(0, MAX_VISIBLE_CHAINS);
  const overflowCount = sortedChainIds.length - MAX_VISIBLE_CHAINS;

  useEffect(() => {
    if (selected) {
      disconnectRef.current?.focus();
    }
  }, [selected, disconnectRef]);

  return (
    <DropdownMenu.Item
      key={wallet.address}
      className="outline-none px-2 flex items-center justify-center h-16 focus:outline-none data-[highlighted]:outline-none"
      onKeyDown={(event) => {
        if (event.key === 'ArrowDown') {
          onNext?.();
        } else if (event.key === 'ArrowUp') {
          onPrevious?.();
        } else if (event.key === 'Enter') {
          onDisconnect(wallet);
        }
      }}
      onSelect={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      {wallet && <WalletAvatar wallet={wallet} size={32} />}
      <div
        className={fontStyles({
          variant: 'LABEL_SM',
          class: [
            'flex flex-col flex-grow pl-4 pb-1 [&::after]:content-none [&::before]:content-none',
          ],
        })}
      >
        {wallet && <ShortenedAddress isEthereum={!isSolana} wallet={wallet} className="-ml-1.5" />}
        <Tooltip.Provider>
          <Tooltip.Root>
            <Tooltip.Trigger asChild>
              <div
                tabIndex={0}
                aria-label="Portfolio value and chain breakdown"
                className="flex items-center pr-1 text-token-gray-800"
              >
                {isPortfolioLoading ? (
                  <TextSkeleton width={80} height={16} />
                ) : portfolio ? (
                  <>
                    <span>{formatFiat(portfolio.totalValue)}</span>
                    {!isSolana && visibleChains.length > 0 && (
                      <>
                        <span className="mx-1 text-token-gray-700">on</span>
                        <div className="flex items-center">
                          {visibleChains.map((chainId, i) => (
                            <ChainAvatar
                              key={chainId}
                              chainId={chainId}
                              size={14}
                              className={
                                i > 0
                                  ? '-ml-1 shadow-[0_0_0_1px_var(--color-special-background-modal)]'
                                  : undefined
                              }
                            />
                          ))}
                          {overflowCount > 0 && (
                            <span className="-ml-1 h-3.5 rounded-full bg-token-gray-300 px-1 text-[10px] font-semibold leading-[14px] text-token-gray-700 shadow-[0_0_0_1px_var(--color-special-background-modal)]">
                              +{overflowCount}
                            </span>
                          )}
                        </div>
                      </>
                    )}
                  </>
                ) : (
                  <span>—</span>
                )}
              </div>
            </Tooltip.Trigger>
            {!isSolana && chainDistribution && sortedChainIds.length > 0 && (
              <Tooltip.Portal>
                <Tooltip.Content className="p-3" side="bottom" align="start">
                  <div className="flex flex-col gap-2">
                    {sortedChainIds.map((chainId) => (
                      <div key={chainId} className="flex items-center justify-between gap-6">
                        <div className="flex items-center gap-1.5">
                          <ChainAvatar chainId={chainId} size={20} />
                          <span className="text-[13px] text-token-gray-900">
                            {NETWORK_NAME_PER_CHAIN_ID[chainId]}
                          </span>
                        </div>
                        <span className="text-[13px] text-token-gray-1600">
                          {formatFiat(chainDistribution[chainId])}
                        </span>
                      </div>
                    ))}
                  </div>
                </Tooltip.Content>
              </Tooltip.Portal>
            )}
          </Tooltip.Root>
        </Tooltip.Provider>
      </div>
      <Tooltip.Provider>
        <Tooltip.Root>
          <Tooltip.Trigger asChild>
            <button
              tabIndex={selected === undefined ? undefined : selected ? 0 : -1}
              ref={disconnectRef}
              className={buttons({
                size: 'small',
                color: 'transparent',
                shape: 'rounded',
                class: [
                  'flex items-center justify-center',
                  'hover:bg-token-gray-200',
                  'active:bg-token-gray-300',
                  'focus-visible:outline-[3px]',
                  'focus-visible:outline-solid',
                  'focus-visible:outline-token-gray-500',
                  'disabled:text-token-gray-700',
                  'disabled:opacity-[0.5]',
                ],
              })}
              aria-label="Disconnect wallet"
              onClick={() => onDisconnect(wallet)}
            >
              <DisconnectIcon />
            </button>
          </Tooltip.Trigger>
          <Tooltip.Portal>
            <Tooltip.Content>Disconnect</Tooltip.Content>
          </Tooltip.Portal>
        </Tooltip.Root>
      </Tooltip.Provider>
    </DropdownMenu.Item>
  );
}

function DropdownTrigger({ onConnect }: { onConnect: () => void }) {
  const { solanaWallet, ethereumWallet } = useMatchaWallets();
  const { data: ensName } = useEnsName({ address: ethereumWallet?.address as EthereumAddress });

  const activeWallets = useMemo(
    () => [ethereumWallet, solanaWallet].filter((val) => val !== undefined),
    [ethereumWallet, solanaWallet],
  );

  if (!solanaWallet && !ethereumWallet) {
    return (
      <button onClick={onConnect} className={btnStyles.PRIMARY_MD}>
        Connect
      </button>
    );
  }

  return (
    <DropdownMenu.Trigger
      className={buttons({
        color: 'gray',
        shape: 'rounded',
        size: 'small',
        class: [
          'h-10 rounded-[26px] shrink-0 shadow-05 pl-2 pr-4 bg-special-background-button-medium-secondary',
          'hover:bg-special-background-button-medium-secondary-hover focus-visible:outline-[3px]',
          'focus-visible:outline-solid focus-visible:outline-token-alphalight-500 active:outline-none',
          'active:bg-special-background-button-medium-secondary-pressed',
        ],
      })}
    >
      <span className="flex items-center ml-1">
        <AvatarStack size={24} overlap={2}>
          {activeWallets.map((wallet) => (
            <WalletAvatar key={wallet.address} wallet={wallet} size={24} />
          ))}
        </AvatarStack>
        <span className={fontStyles({ variant: 'LABEL_MD', class: 'ml-2.5' })}>
          {activeWallets.length > 1
            ? 'Wallets'
            : getWalletName(activeWallets[0], ensName, activeWallets[0] === ethereumWallet)}
        </span>
        <ChevronDownIcon className="ml-2 shrink-0" />
      </span>
    </DropdownMenu.Trigger>
  );
}

function getWalletName(
  activeWallet: ConnectedStandardSolanaWallet | ConnectedWallet,
  ensName: GetEnsNameReturnType | undefined,
  isEthereum: boolean,
) {
  if (isEthereum) {
    const address = activeWallet.address as EthereumAddress;
    if (ensName) {
      return ensName;
    }
    return formatShortAddress(address);
  }
  const address = activeWallet.address as SolanaAddress;
  return formatShortAddress(address);
}
